import api from './api';

export type Translation = string | { [lang: string]: string };
export type AssessmentMeasurement = 'initial' | 'final' | 'followup';

export interface ScaleConfig {
  min: number;
  max: number;
  step?: number;
  minLabel?: Translation;
  maxLabel?: Translation;
}

export interface WeightedMcqOption {
  text: Translation;
}

export interface AssessmentLinkedQuestion {
  _id: string;
  linkedKey: string;
  type: 'weighted-mcq' | 'scale';
  question: Translation;
  order: number;
  options?: WeightedMcqOption[];
  scale?: ScaleConfig;
}

export interface AssessmentSpecificQuestion {
  _id: string;
  type: 'weighted-mcq' | 'scale' | 'open-text';
  question: Translation;
  order: number;
  options?: WeightedMcqOption[];
  scale?: ScaleConfig;
}

export interface AssessmentEligibility {
  diagnosticRequired: boolean;
  diagnosticSubmitted: boolean;
  finalImpactAvailable: boolean;
  finalImpactSubmitted: boolean;
  finalImpactBlockedReason: 'workshops' | 'chapters' | 'diagnostic' | 'feature-disabled' | null;
  finalImpactExpectedAfter?: string | null;
  finalImpactUnlockRule?: 'after-last-workshop' | 'when-course-complete';
}

export interface AssessmentOverview {
  enabled: boolean;
  eligibility: AssessmentEligibility;
  initial: { description?: Translation; submitted: boolean; submittedAt?: string | null } | null;
  final: { description?: Translation; submitted: boolean; submittedAt?: string | null } | null;
  followups: Array<{
    id: string;
    label: Translation;
    description?: Translation;
    order: number;
    unlockDate: string | null;
    available: boolean;
    pendingDiagnostic?: boolean;
    submitted: boolean;
    submittedAt: string | null;
  }>;
}

export interface AssessmentForm {
  measurement: AssessmentMeasurement;
  followupId?: string;
  description?: Translation;
  label?: Translation;
  linkedQuestions: AssessmentLinkedQuestion[];
  specificQuestions: AssessmentSpecificQuestion[];
}

export type AssessmentAnswerPayload =
  | { linkedKey: string; selectedOptionIndex: number }
  | { linkedKey: string; scaleValue: number }
  | { questionId: string; selectedOptionIndex: number }
  | { questionId: string; scaleValue: number }
  | { questionId: string; textValue: string };

export const assessmentApi = {
  getCourseAssessments: async (courseId: string): Promise<AssessmentOverview> => {
    const response = await api.get(`/student/courses/${courseId}/assessments`);
    return response.data;
  },

  getAssessment: async (
    courseId: string,
    measurement: AssessmentMeasurement,
    followupId?: string
  ): Promise<AssessmentForm> => {
    const url =
      measurement === 'followup' && followupId
        ? `/student/courses/${courseId}/assessments/${measurement}/${followupId}`
        : `/student/courses/${courseId}/assessments/${measurement}`;
    const response = await api.get(url);
    return response.data;
  },

  submitAssessment: async (
    courseId: string,
    measurement: AssessmentMeasurement,
    answers: AssessmentAnswerPayload[],
    followupId?: string
  ): Promise<{ id: string; submittedAt: string }> => {
    const url =
      measurement === 'followup' && followupId
        ? `/student/courses/${courseId}/assessments/${measurement}/${followupId}/submit`
        : `/student/courses/${courseId}/assessments/${measurement}/submit`;
    const response = await api.post(url, { answers });
    return response.data;
  },
};
